import { onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { isCoBrowsingActive } from "@/shared/live-person/composables/useLivePerson";
import type { autocomplete } from "@algolia/autocomplete-js";

interface IUseCoBrowseSearchOptions {
  // The autocomplete instance is created after mount, so it is resolved lazily.
  getAutocomplete: () => ReturnType<typeof autocomplete> | undefined;
  setSuppressPanel: (suppress: boolean) => void;
  // Invoked after a post-submit correction navigates to a new term (emit + GTM in the component).
  onSearchCorrected: (query: string) => void;
}

/**
 * LivePerson co-browse search handling (TL-3774).
 * LP replays agent keystrokes as programmatic input.value writes that fire no input events,
 * so Algolia's state.query and app listeners never see them until submit time.
 */
export function useCoBrowseSearch({ getAutocomplete, setSuppressPanel, onSearchCorrected }: IUseCoBrowseSearchOptions) {
  const router = useRouter();

  let inputPollId: number | undefined;
  let recheckTimeoutId: number | undefined;
  let lastNonEmptyInput = "";
  let lastSubmittedQuery = "";
  // Mirror of Algolia's state.query (kept in sync via updateAlgoliaQueryMirror) for drift detection.
  let algoliaQueryMirror = "";

  watch(
    isCoBrowsingActive,
    (active) => {
      if (active && inputPollId === undefined) {
        inputPollId = window.setInterval(() => {
          const el = document.querySelector(".aa-Input") as HTMLInputElement | null;
          if (!el) {
            return;
          }
          const value = el.value.trim();
          if (value) {
            lastNonEmptyInput = value;
          }
          // Sync Algolia state to the DOM so re-renders can't restore a stale term (which LP would
          // mirror back as "deleted characters reappearing"). Compare trimmed (whitespace-only
          // replay diffs must not reopen the panel), but sync the raw value (keep trailing spaces).
          if (value !== algoliaQueryMirror.trim()) {
            algoliaQueryMirror = el.value;
            const ac = getAutocomplete();
            ac?.setQuery(el.value);
            // No input events fire for agent keystrokes — re-run sources here so the dropdown updates.
            if (value && value === lastSubmittedQuery) {
              ac?.setIsOpen(false);
            } else if (value) {
              lastSubmittedQuery = "";
              setSuppressPanel(false);
              ac?.setIsOpen(true);
              void ac?.refresh();
            } else {
              ac?.setIsOpen(false);
            }
          }
        }, 250);
      } else if (!active && inputPollId !== undefined) {
        window.clearInterval(inputPollId);
        inputPollId = undefined;
        lastNonEmptyInput = "";
        lastSubmittedQuery = "";
      }
    },
    { immediate: true },
  );

  // Records a submitted term so replay can't blank the box or trigger a stale resubmit.
  const recordSubmittedQuery = (query: string) => {
    lastSubmittedQuery = query;
    lastNonEmptyInput = query;
    algoliaQueryMirror = query;
  };

  // LP replay can deliver the submit before the value mutations for the new term. Re-read the
  // input shortly after each co-browse submit and correct the navigation if a different term landed.
  const scheduleSubmitRecheck = (submittedQuery: string) => {
    if (recheckTimeoutId !== undefined) {
      window.clearTimeout(recheckTimeoutId);
    }
    recheckTimeoutId = window.setTimeout(() => {
      recheckTimeoutId = undefined;
      if (!isCoBrowsingActive.value) {
        return;
      }
      const firstRead = (document.querySelector(".aa-Input") as HTMLInputElement | null)?.value?.trim() || "";
      if (!firstRead || firstRead === submittedQuery) {
        return;
      }
      // Confirm the value is stable (not mid-typing) before correcting the navigation.
      recheckTimeoutId = window.setTimeout(() => {
        recheckTimeoutId = undefined;
        if (!isCoBrowsingActive.value) {
          return;
        }
        const inputEl = document.querySelector(".aa-Input") as HTMLInputElement | null;
        const currentValue = inputEl?.value?.trim() || "";
        if (!currentValue || currentValue === submittedQuery || currentValue !== firstRead) {
          return;
        }
        recordSubmittedQuery(currentValue);
        setSuppressPanel(true);
        const ac = getAutocomplete();
        ac?.setQuery(currentValue);
        if (inputEl && inputEl.value !== currentValue) {
          inputEl.value = currentValue;
        }
        void router.replace({ name: "Search", query: { q: currentValue } });
        ac?.setIsOpen(false);
        document.querySelector(".aa-Panel")?.remove();
        onSearchCorrected(currentValue);
      }, 250);
    }, 400);
  };

  // Co-browse query precedence for onSubmit: DOM value, then last polled value, then Algolia state.
  const resolveSubmitQuery = (domValue: string, stateQuery: string) => domValue || lastNonEmptyInput || stateQuery;

  // Replayed submit with a cleared input where every fallback resolves to the already-searched term.
  const isStaleReplayedSubmit = (domValue: string, searchQuery: string) =>
    isCoBrowsingActive.value && !domValue && searchQuery === lastSubmittedQuery;

  // Keeps the polled fallback fresh when a real input event fires during co-browse.
  const noteTypedValue = (value: string) => {
    if (isCoBrowsingActive.value && value.trim()) {
      lastNonEmptyInput = value.trim();
    }
  };

  const updateAlgoliaQueryMirror = (query: string) => {
    algoliaQueryMirror = query;
  };

  onUnmounted(() => {
    if (inputPollId !== undefined) {
      window.clearInterval(inputPollId);
      inputPollId = undefined;
    }
    if (recheckTimeoutId !== undefined) {
      window.clearTimeout(recheckTimeoutId);
      recheckTimeoutId = undefined;
    }
  });

  return {
    resolveSubmitQuery,
    isStaleReplayedSubmit,
    recordSubmittedQuery,
    scheduleSubmitRecheck,
    noteTypedValue,
    updateAlgoliaQueryMirror,
  };
}
